import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { sound } from "@pixi/sound";
import { gsap } from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { PixiPlugin } from "gsap/PixiPlugin";
import * as PIXI from "pixi.js";
import { BitmapText, Container } from "pixi.js";
import { Tween } from "tweedle.js";
import { valMap } from "../config/getSymbol";
import { betValueGlobal, isSuperQuickBtnOnGlobal } from "../config/GlobalVariableConfig";
import { REEL_NUMBERS } from "../config/reelConfig";
import { GameConstants } from "../gameClientEvents/GameConstants";
import { wait } from "../utils";
import { ParallelTask, Task } from "../utils/ParallelTask";
import { BLANK_SYM_ID, reelOverlayObject, SYMBOLS_PER_REEL_FEATURE } from "./ReelInit";
import { ReelSpin } from "./ReelSpin";

gsap.registerPlugin(PixiPlugin, MotionPathPlugin);
PixiPlugin.registerPIXI(PIXI);

export class ReelWin extends ReelSpin {

    private readonly WAVE_AMPLITUDE = 40;
    // private readonly WAVE_FREQUENCY = 1.5;
    // private readonly TRAVEL_DURATION_MS = 300;
    // private readonly TRAVEL_DURATION_QUICK_MS = 100;
    private transitionBeforeWin: Spine | null = null;

    constructor() {
        super();

    }

    public updateReelWin() {
        this.updateReelSpin();
    }

    private updateTotalWinText(): void {
        const winningValue = this.totalWinVal * betValueGlobal;
        this.totalWinText.text = (winningValue > 0) ? winningValue.toFixed(2) : "";
        this.totalWinText.visible = winningValue > 0;
        this.totalWinText.renderable = winningValue > 0;
        this.totalWinText.alpha = (winningValue > 0) ? 1 : 0;
        while(this.totalWinText.width > (this.winCollectionMeter.width - 20)){
            this.totalWinText.style.fontSize -= 2;
        }
    }

    public async playTransitionBeforeWin(): Promise<void> {
        if (!this.transitionBeforeWin) {
            this.transitionBeforeWin = Spine.from({
                skeleton: "holdWinTransitionSkel",
                atlas: "holdWinTransitionAtlas"
            });
            this.transitionBeforeWin.label = "transitionBeforeWin";
            this.transitionBeforeWin.position.set(560, 540);
            this.transitionBeforeWin.visible = false;
            this.addChild(this.transitionBeforeWin);
        }

        return new Promise<void>((resolve) => {
            if (!this.transitionBeforeWin) {
                resolve();
                return;
            }

            this.addChild(this.transitionBeforeWin);
            this.transitionBeforeWin.visible = true;
            const timeScale = isSuperQuickBtnOnGlobal ? 2 : 1;
            sound.play('dynamite_blast', { volume: 1, speed: timeScale });
            this.transitionBeforeWin.state.setAnimation(0, "animation", false).listener = {
                complete: () => {
                    this.transitionBeforeWin.state.clearListeners();
                    this.transitionBeforeWin.visible = false;
                    resolve();
                }
            };
        });
    }


    // this is main function which is doing the animation -
    public async animateAllWinningSymbols(): Promise<void> {

        this.spinLeftMeter.visible = false;
        this.winCollectionMeter.visible = true;
        this.winCollectionMeter.state.setAnimation(0, "digital_pannel_appear", false);

        this.totalWinVal = 0;
        this.updateTotalWinText();

        const collectableCoins = this.getCollectableCoins();
        let tasks: Task<any>[] = [];

        for (const coin of collectableCoins) {
            const reelSymbol = this.reelOverlaySymbols[coin.reelNum][coin.posNum];
            if (!reelSymbol) continue;

            const symbolId = reelSymbol.symbolId;
            if ([GameConstants.MINI_SYMBOL_ID, GameConstants.MINOR_SYMBOL_ID, GameConstants.MAJOR_SYMBOL_ID, GameConstants.GRAND_SYMBOL_ID].includes(symbolId)) {
                await this.handleJackpotFlip(reelSymbol, coin.valueToCollect);
            }
        }

        for (const coin of collectableCoins) {

            const reelSymbol = this.reelOverlaySymbols[coin.reelNum][coin.posNum];
            if (!reelSymbol) continue;

            tasks.push(async () => {

                // 2️⃣ Play collect animation
                await this.playSymbolCollectAnimation(reelSymbol);

                // 3️⃣ Collect to meter
                await this.collectCoinToMeterAnimation(
                    coin.reelNum,
                    coin.posNum,
                    coin.valueToCollect
                );

                if (!isSuperQuickBtnOnGlobal) {
                    await wait(70);
                }
            });
        }

        if (tasks.length === 0) return;

        const masterTask = new ParallelTask(tasks);
        await masterTask.execute({ stagger: 500, waitStrategy: 'all' });
    }

    private playSymbolCollectAnimation(reelSymbol: reelOverlayObject): Promise<void> {
        return new Promise(resolve => {

            reelSymbol.symbol.state
                .setAnimation(0, "collect", false)
                .listener = {
                complete: () => {
                    this.tintCoinTextOnCollect(reelSymbol, 0x555555);
                    reelSymbol.symbol.state.clearListeners();
                    resolve();
                }
            };
        });
    }

    private tintCoinTextOnCollect(reelSymbol: reelOverlayObject, tintColor: number): void {
        reelSymbol.coinValText && (reelSymbol.coinValText.tint = tintColor);
        reelSymbol.coinValFinalText && (reelSymbol.coinValFinalText.tint = tintColor);

        const slotObject = reelSymbol.symbol?.getSlotObject("multiplier") as any;
        if (!slotObject) {
            return;
        }

        if (slotObject instanceof BitmapText) {
            slotObject.tint = tintColor;
            return;
        }

        if (slotObject instanceof Container) {
            for (const child of slotObject.children) {
                if (child instanceof BitmapText) {
                    child.tint = tintColor;
                }
            }
        }
    }

    private handleJackpotFlip(reelSymbol: reelOverlayObject, coinVal: number): Promise<void> {
        return new Promise(resolve => {

            const trackEntry = reelSymbol.symbol.state.setAnimation(0, "flip", false);

            trackEntry.listener = {
                event: (_entry: any, event: any) => {
                    if (event.data.name === "reveal") {

                        const coinValText = reelSymbol.coinValText;
                        if (!coinValText) return;

                        const wrapper = new Container();
                        wrapper.addChild(coinValText);
                        coinValText.text = `${coinVal}x`;

                        coinValText.anchor.set(0.5);

                        const scale = Math.min(80 / coinValText.width, 1);
                        coinValText.scale.set(scale);

                        reelSymbol.symbol.addSlotObject("multiplier", wrapper);
                    }
                },
                complete: () => {
                    reelSymbol.symbol.state.clearListeners();
                    resolve();
                }
            };
        });
    }

    private getCollectableCoins(): { reelNum: number, posNum: number, valueToCollect: number }[] {
        const collectableCoins: { reelNum: number, posNum: number, valueToCollect: number }[] = [];

        for (let reelNum = 0; reelNum < REEL_NUMBERS; reelNum++) {
            for (let posNum = 0; posNum < SYMBOLS_PER_REEL_FEATURE; posNum++) {
                const reelSymbol = this.reelOverlaySymbols[reelNum][posNum];
                if (!reelSymbol || !reelSymbol.view.visible) {
                    continue;
                }

                if (reelSymbol.symbolId == null || reelSymbol.symbolId === BLANK_SYM_ID) {
                    continue;
                }

                const coinValue = valMap[reelSymbol.symbolId] || 0;
                if (coinValue <= 0) {
                    continue;
                }

                reelSymbol.coinVal = coinValue;
                collectableCoins.push({ reelNum, posNum, valueToCollect: coinValue });
            }
        }

        return collectableCoins;
    }

    private collectCoinToMeterAnimation(reelNum: number, posNum: number, valueToCollect: number): Promise<void> {
        return new Promise<void>((resolve) => {
            const reelSymbol = this.reelOverlaySymbols[reelNum][posNum];
            if (!reelSymbol) {
                resolve();
                return;
            }

            //const sourceText = reelSymbol.coinValFinalText ?? reelSymbol.coinValText;
            const flyText = new BitmapText({
                text: `${valueToCollect}x`,
                style: {
                    fontFamily: "coin_font",
                    fontSize: 60,
                }
            });

            flyText.anchor.set(0.5);
            flyText.visible = false;
            this.addChild(flyText);

            const start = this.toLocal(reelSymbol.view.getGlobalPosition());
            const end = this.toLocal(this.winCollectionMeter.getGlobalPosition());

            const duration = 0.8;


            const animationState = { p: 0 };

            gsap.to(animationState, {
                p: 1,
                duration: duration,
                ease: "expo.in",
                onUpdate: () => {
                    flyText.visible = true;
                    const p = animationState.p;

                    flyText.x = start.x + (end.x - start.x) * p;
                    flyText.y = start.y + (end.y - start.y) * p + (Math.sin(p * Math.PI) * this.WAVE_AMPLITUDE);

                    flyText.angle = p * 360;

                    const s = 1 - (0.25 * p);
                    flyText.scale.set(s);
                },
                onComplete: () => {

                    if (flyText.parent) flyText.parent.removeChild(flyText);
                    flyText.destroy();

                    this.totalWinVal = Math.min(this.totalWinVal + valueToCollect, 10000);
                    this.updateTotalWinText();

                    const timeScale = isSuperQuickBtnOnGlobal ? 2 : 1;
                    sound.play('multiplier_panel', { volume: 1, speed: timeScale });
                    this.winCollectionMeter.state.setAnimation(0, "digital_pannel_collect", false).listener = {
                        complete: () => {
                        }
                    }

                    this.punchScale(this.totalWinText, () => resolve());
                }
            });
        });
    }

    private punchScale(
        target: BitmapText,
        onDone?: () => void,
        peakScale = 1.15,
        halfDuration = 90
    ): void {
        const scaleObj = { s: 1 };

        new Tween(scaleObj)
            .to({ s: peakScale }, halfDuration)
            .onUpdate(() => target.scale.set(scaleObj.s))
            .onComplete(() => {
                new Tween(scaleObj)
                    .to({ s: 1 }, halfDuration)
                    .onUpdate(() => target.scale.set(scaleObj.s))
                    .onComplete(() => onDone?.())
                    .start();
            })
            .start();
    }

    //@ts-ignore
    private collectTrailAnimation(reelNum: number, posNum: number, valueToCollect: number): Promise<void> {
        return new Promise<void>((resolve) => {
            const animationIndex = reelNum * SYMBOLS_PER_REEL_FEATURE + posNum;
            const animationNumber = animationIndex + 1;

            const listener = {
                event: (_entry: any, event: any) => {
                    if (event.data.name === "trail_hit") {
                        this.totalWinVal += valueToCollect;
                        this.totalWinVal = Math.min(this.totalWinVal, 10000);
                        this.updateTotalWinText();
                        resolve();
                    }
                }
            };

            this.collectTrail.state.addListener(listener);

            const timeScale = isSuperQuickBtnOnGlobal ? 2 : 1;
            this.collectTrail.state.setAnimation(animationNumber, `bonus_reel_trail_${animationNumber}`, false);
            this.collectTrail.state.timeScale = timeScale;
            sound.play("win_collection_trail", { volume: 1, speed: timeScale });
        });
    }

    public async lineToggle(currentRespinIndex: number): Promise<void> {
        return new Promise(async (resolve) => {
            console.log("inside lineToggle", currentRespinIndex);
            resolve();
        });
    }
}
